import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { MatLegacyDialogConfig as MatDialogConfig } from "@angular/material/legacy-dialog";
import { Title } from "@angular/platform-browser";
import { BehaviorSubject, Observable, ReplaySubject, Subject, of, forkJoin } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import {
  SocialIconPathEnum,
  SocialNetworkEnum,
  SubTypeEnum,
} from "src/app/shared/enums/enums";
import { TabKeyEnum } from "src/app/shared/enums/tab-key-enum";
import {
  CustomTabIconDataInterface,
  TabInterface,
  TabViewOnInterface,
} from "src/app/shared/interfaces/tab-interface";
import { ENV_BUILD, environment } from "src/environments/environment";
import { GroupsService } from "../groups/groups.service";
import {
  AccessOptions,
  GroupVisibilityStatus,
  NotificationPreferenceStatus,
} from "../groups/models/group-detail.model";
import { ExdrLanguageService } from "../languages/services/exdr-language.service";
import { StreamDesignType } from "../profile/custom-tabs/tab-form/tab-form.component";
import { PageTitleEnum } from "./enums/page-title.enum";
import { Addon, PublicProperties, FeedOptions } from "./models/addon.model";
import { AUTH_TYPE_HEADER, AuthService, AuthType } from "./services/auth.service";
import { EspTokenResolverService } from "./services/esp-auth.service";
import { ActivatedRoute, Router } from "@angular/router";

@Injectable({ providedIn: "root" })
export class SharedService {
  public isDefaultTopic: boolean = false;
  private _overlayStatusSubject: Subject<any> = new Subject<any>();
  bannerSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  footerSubject$: BehaviorSubject<any> = new BehaviorSubject<any>(false);
  reloadTabGroup$: BehaviorSubject<any> = new BehaviorSubject<any>(false);
  sendNewtabGroup$: BehaviorSubject<{ title: string; group: string; tabs: TabInterface[] } | null> = new BehaviorSubject(null);
  private groupIdParamSource = new ReplaySubject<string>(1);
  public groupIdParam = this.groupIdParamSource.asObservable();
  private _prevGroupId = "";
  public addonUpdate$: Subject<void> = new Subject<void>();
  public addonDelete$: Subject<string> = new Subject<string>();
  public myDayTabLoaded$: Subject<boolean> = new Subject<boolean>();
  public isCreatingGroup: boolean = false;
  public selectedTabKey$: Subject<string> = new Subject<string>();

  private routerData = new Subject<any>();
  private routerTeamData = new Subject<any>();
  public firstHeaderTabPath = "";
  private isRequestTabClicked = new Subject<any>();
  private isHighPriorityActivity = new Subject<any>();
  private profileData = new Subject<any>();
  private measureList = new Subject<any>();
  private currentUrl = new BehaviorSubject<any>('');

  tabs: TabInterface[] = [];
  public inputTextDirection$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  // private reloadkeyResults = new Subject<any>();
  private reloadkeyResults = new Subject<{ data: any, boardId?: any }>()
  eventTriggered$ = this.reloadkeyResults.asObservable();
  // Sending tab data to nav-inner-tab-groups
  private innerTabDataSubject = new Subject<TabInterface>();
  sendInnerTabData$ = this.innerTabDataSubject.asObservable();
  public clearInnerTabDataSUbject = new Subject<boolean>();

  // BehaviorSubject for passing mainDefinitionVisibilityType data
  private mainDefinitionVisibilityTypeSubject = new BehaviorSubject<string | null>(null);
  mainDefinitionVisibilityType$ = this.mainDefinitionVisibilityTypeSubject.asObservable();

  constructor(
    private _exdrLangService: ExdrLanguageService,
    private groupsService: GroupsService,
    private authService: AuthService,
    private espTokenResolver: EspTokenResolverService,
    private titleService: Title,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    private http: HttpClient,
    private router: Router
  ) { }

  setMessage(message: boolean) {
    this.isRequestTabClicked.next(message);
  }

  getMessage(): Observable<any> {
    return this.isRequestTabClicked.asObservable();
  }

  // Emit boolean values to subscribers
  sendClearBoolean(value: boolean) {
    this.clearInnerTabDataSUbject.next(value);
  }

  // Allow components to subscribe to the boolean values
  getCLearBoolean() {
    return this.clearInnerTabDataSUbject.asObservable();
  }

  sendInnerTabData(data: TabInterface) {
    this.innerTabDataSubject.next(data);
  }

  setHighPriorityActivity(message: any) {
    this.isHighPriorityActivity.next(message);
  }

  getHighPriorityActivity(): Observable<any> {
    return this.isHighPriorityActivity.asObservable();
  }

  setProfileData(message: any) {
    this.profileData.next(message);
  }

  getProfileData(): Observable<any> {
    return this.profileData.asObservable();
  }

  setMeasureList(list: any) {
    this.measureList.next(list);
  }

  getMeasureList(): Observable<any> {
    return this.measureList.asObservable();
  }

  emitAddonUpdate() {
    this.authService.clearCachedAddons(this.authService.idenediGroupId);
    this.addonUpdate$.next();
  }

  emitAddonDelete(key: string) {
    this.addonDelete$.next(key);
  }

  overlayStatus() {
    return this._overlayStatusSubject;
  }

  hideOverlay() {
    this._overlayStatusSubject.next(false);
  }

  showOverlay() {
    this._overlayStatusSubject.next(true);
  }

  setCurrentUrl(url: string) {
    this.currentUrl.next(url);
  }

  getCurrentUrl(): Observable<any> {
    return this.currentUrl.asObservable();
  }

  reloadkeyResultsEvent(data: any, boardId?: any) {
    // this.reloadkeyResults.next(data,boardId);
    this.reloadkeyResults.next({ data, boardId });
  }
  getReloadkeyResultsEvent(): Observable<any> {
    return this.reloadkeyResults.asObservable();
  }

  // Methods for mainDefinitionVisibilityType Subject
  setMainDefinitionVisibilityType(visibilityType: string) {
    this.mainDefinitionVisibilityTypeSubject.next(visibilityType);
  }

  getMainDefinitionVisibilityType(): Observable<string | null> {
    return this.mainDefinitionVisibilityTypeSubject.asObservable();
  }

  changeGroupIdParam(id) {
    if (this._prevGroupId != id) {
      this._prevGroupId = id;
      this.groupIdParamSource.next(id);
    }
  }

  setFirstHeaderTab(firstTabPath: string): void {
    this.firstHeaderTabPath = firstTabPath;
  }

  getDefaultDialogConfig(): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    dialogConfig.width = "37.5rem";
    (dialogConfig.maxWidth = "90vw"),
      (dialogConfig.disableClose = true);
    dialogConfig.closeOnNavigation = true;
    return dialogConfig;
  }

  getDefaultAddonPublicProperties(): PublicProperties {
    return {
      access_token: "",
      baseURL: "",
      domain: "",
      footerDescription: "",
      footerTitle: "",
      groupIdenedi: "",
      homeTopicId: "",
      jsonData: "{\n\n}",
      language: "en",
      logoUrl: "",
      organizationName: "",
      organziationId: "",
      powerBiData: "",
      refresh_token: "",
      title: "",
    };
  }

  initEspLoginDialogConfig(): MatDialogConfig {
    const dialogConfig: MatDialogConfig = new MatDialogConfig();
    dialogConfig.data = {};
    dialogConfig.disableClose = true;
    dialogConfig.data.title = "ExdrCommon.Info";
    dialogConfig.data.body = "ExdrCommon.EspNoAccess";
    dialogConfig.data.okText = "ExdrCommon.OK";
    dialogConfig.direction = this._exdrLangService.currentLanguage.isRtl
      ? "rtl"
      : "ltr";
    return dialogConfig;
  }

  getGroupVisibilities(): {
    key: GroupVisibilityStatus;
    name: string;
    description: string;
  }[] {
    return [
      {
        key: GroupVisibilityStatus.Public,
        name: "GroupVisibilityInfo.Public",
        description: "GroupVisibilityInfo.PublicDesc",
      },
      {
        key: GroupVisibilityStatus.Private,
        name: "GroupVisibilityInfo.Private",
        description: "GroupVisibilityInfo.PrivateDesc",
      },
      {
        key: GroupVisibilityStatus.Secret,
        name: "GroupVisibilityInfo.Invisible",
        description: "GroupVisibilityInfo.InvisibleDesc",
      },
      {
        key: GroupVisibilityStatus.Incognito,
        name: "GroupVisibilityInfo.Incognito",
        description: "GroupVisibilityInfo.IncognitoDesc",
      },
    ];
  }

  getNotificationPreference(): {
    key: NotificationPreferenceStatus;
    name: string;
  }[] {
    return [
      {
        key: NotificationPreferenceStatus.App,
        name: "NotificationPreferenceInfo.App",
      },
      {
        key: NotificationPreferenceStatus.Email,
        name: "NotificationPreferenceInfo.Email",
      },
      {
        key: NotificationPreferenceStatus.Sms,
        name: "NotificationPreferenceInfo.Sms",
      },
      {
        key: NotificationPreferenceStatus.WhatsApp,
        name: "NotificationPreferenceInfo.WhatsApp",
      },
      {
        key: NotificationPreferenceStatus.None,
        name: "NotificationPreferenceInfo.None",
      }
    ]
  }

  getGroupAccessOptions(): {
    key: AccessOptions;
    name: string;
  }[] {
    return [
      {
        key: AccessOptions.LoginRequired,
        name: "AccessOptions.LoginRequired",
      },
      {
        key: AccessOptions.LoginOptional,
        name: "AccessOptions.LoginOptional",
      },
      {
        key: AccessOptions.PublicOnly,
        name: "AccessOptions.PublicOnly",
      },
    ];
  }

  getAllowedImagesFormats() {
    return [".jpg", ".png", ".gif", ".bmp", ".jpeg", ".webp", ".avif", ".JPG"];
  }

  getAllowedDocumentsFormats() {
    return [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt"];
  }

  getAllowedVideosFormats() {
    return [".mp4"];
  }

  areArraysDifferent(arrayA: string[], arrayB: string[]) {
    if (arrayA.length == arrayB.length) {
      for (const itemA of arrayA) {
        let found = false;
        for (const itemB of arrayB) {
          if (itemA == itemB) {
            found = true;
            break;
          }
        }
        if (!found) {
          return true;
        }
      }
    } else {
      return true;
    }

    return false;
  }

  hasGetUserMedia() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }

  getViewOnData(): TabViewOnInterface[] {
    return [
      {
        id: 1,
        path: "/assets/images/ic-mobile.svg",
        active: false,
        name: "Mobile",
      },
      {
        id: 2,
        path: "/assets/images/ic-web.svg",
        active: false,
        name: "Web",
      },
    ];
  }

  getCustomTabIcons(id?: number): CustomTabIconDataInterface[] {
    const customTabsIconMap = [
      {
        id: 1,
        path: "/assets/images/custom-tab/1.svg",
      },
      {
        id: 2,
        path: "/assets/images/custom-tab/2.svg",
      },
      {
        id: 3,
        path: "/assets/images/custom-tab/3.svg",
      },
      {
        id: 4,
        path: "/assets/images/custom-tab/4.svg",
      },
      {
        id: 5,
        path: "/assets/images/custom-tab/5.svg",
      },
      {
        id: 6,
        path: "/assets/images/custom-tab/6.svg",
      },
      {
        id: 7,
        path: "/assets/images/custom-tab/7.svg",
      },
      {
        id: 8,
        path: "/assets/images/custom-tab/8.svg",
      },
      {
        id: 9,
        path: "/assets/images/custom-tab/9.svg",
      },
      {
        id: 10,
        path: "/assets/images/custom-tab/10.svg",
      },
      {
        id: 11,
        path: "/assets/images/custom-tab/11.svg",
      },
      {
        id: 12,
        path: "/assets/images/custom-tab/12.svg",
      },
      {
        id: 13,
        path: "/assets/images/custom-tab/13.svg",
      },
      {
        id: 14,
        path: "/assets/images/custom-tab/14.svg",
      },
      {
        id: 15,
        path: "/assets/images/custom-tab/15.svg",
      },
      {
        id: 16,
        path: "/assets/images/custom-tab/16.svg",
      },
      {
        id: 17,
        path: "/assets/images/custom-tab/17.svg",
      },
      {
        id: 18,
        path: "/assets/images/custom-tab/18.svg",
      },
      {
        id: 19,
        path: "/assets/images/custom-tab/19.svg",
      },
      {
        id: 20,
        path: "/assets/images/custom-tab/20.svg",
      },
      {
        id: 21,
        path: "/assets/images/custom-tab/21.svg",
      },
      {
        id: 22,
        path: "/assets/images/custom-tab/22.svg",
      },
      {
        id: 23,
        path: "/assets/images/custom-tab/23.svg",
      },
    ];
    if (id) {
      return customTabsIconMap.filter((t) => t.id === id);
    }

    return customTabsIconMap;
  }
  getHeaderTabs(filter?: {
    key?: string;
    excluded?: TabKeyEnum[];
  }): TabInterface[] {
    const defaultTabs: TabInterface[] = [
      {
        key: TabKeyEnum.Chat,
        iconPath: "/assets/images/icons/menu-icons/ic-messages.svg",
        path: "/pages/messages",
        name: "Chat",
        iseditable: 1,
        isvisible: 1,
        order: 1,
        utilityType: "icon",
        isFixed: "1",
      },
      {
        key: TabKeyEnum.Search,
        iconPath: "/assets/images/search-icon.svg",
        path: "/pages/search",
        name: "Search",
        iseditable: 1,
        isvisible: 1,
        order: 2,
        utilityType: "icon",
        isFixed: "1",
      },
      {
        key: TabKeyEnum.Language,
        iconPath: "/assets/images/icons/menu-icons/ic-messages.svg",
        path: "/pages/messages",
        name: "Language",
        iseditable: 0,
        isvisible: 1,
        order: 2,
        utilityType: "icon",
        isFixed: "1",
      },
      {
        key: TabKeyEnum.SocialMedia,
        iconPath: "/assets/images/icons/menu-icons/ic-social-media.svg",
        path: "/pages/profile/contacts",
        name: "Contacts",
        iseditable: 1,
        isvisible: 1,
        order: 1,
        utilityType: "text",
        viewOn: "",
      },
      {
        key: TabKeyEnum.EspAttendance,
        iconPath: "/assets/images/icons/menu-icons/clock-icon.svg",
        path: "/pages/profile",
        name: "Clock In / Out",
        iseditable: 1,
        isvisible: 1,
        order: 2,
        utilityType: "text",
        viewOn: "",
        isFixed: "1",
      },
      {
        key: TabKeyEnum.Profile,
        iconPath: "/assets/images/icons/menu-icons/ic-profile.svg",
        path: "/pages/profile",
        name: "Profile",
        iseditable: 1,
        isvisible: 1,
        order: 3,
        utilityType: "text",
      },
      {
        key: TabKeyEnum.Feed,
        iconPath: "/assets/images/icons/menu-icons/ic-news.svg",
        path: "/pages/news",
        name: environment.build === ENV_BUILD.EXCEEDERS ? "News" : "Feed",
        iseditable: 1,
        isvisible: 1,
        order: 1,
      },
      {
        key: TabKeyEnum.Activities,
        iconPath: "/assets/images/icons/menu-icons/ic-activities.svg",
        path: "/pages/activities",
        name: "Activities",
        iseditable: 1,
        isvisible: 1,
        order: 2,
        queryParams: {
          activityType: TabKeyEnum.Activities,
          // isOpenCreateForm: false
        }, // added queryParams to identify SS activity
      },
      {
        key: TabKeyEnum.Requests,
        iconPath: "/assets/images/icons/menu-icons/ic-requests.svg",
        path: "/pages/applications",
        name: "Requests",
        iseditable: 1,
        isvisible: 1,
        order: 3,
      },
      {
        key: TabKeyEnum.EspRecords,
        iconPath: "/assets/images/icons/menu-icons/ic-records.svg",
        path: "/pages/records",
        name: "Records",
        iseditable: 1,
        isvisible: 1,
        order: 4,
      },
      {
        key: TabKeyEnum.EspSurveys,
        iconPath: "/assets/images/icons/menu-icons/ic-records.svg",
        path: "/pages/surveys",
        name: "Surveys",
        iseditable: 1,
        isvisible: 1,
        order: 4,
      },
      {
        key: TabKeyEnum.ScoreCard,
        iconPath: "/assets/images/icons/menu-icons/iconMenuMeasures.svg",
        path: "/pages/scoreCard",
        name: "Score Card",
        iseditable: 1,
        isvisible: 1,
        order: 5,
        queryParams: {
          scorecardType: TabKeyEnum.Activities,
        },

      },
      {
        key: TabKeyEnum.ssnotes,
        iconPath: "/assets/images/icons/menu-icons/ic-activities.svg",
        path: "/pages/notes",
        name: "Notes",
        iseditable: 1,
        isvisible: 1,
        order: 6,
      },
      {
        key: TabKeyEnum.Calendar,
        iconPath: "/assets/images/icons/menu-icons/ic-calendar.svg",
        path: "/pages/events",
        name: "Calendar",
        iseditable: 1,
        isvisible: 1,
        order: 7,
      },
      {
        key: TabKeyEnum.Library,
        iconPath: "/assets/images/icons/menu-icons/ic-products.svg",
        path: "/pages/products",
        name:
          environment.build === ENV_BUILD.EXCEEDERS ? "Products" : "Library",
        iseditable: 1,
        isvisible: 1,
        order: 8,
      },
      {
        key: TabKeyEnum.Members,
        iconPath: "/assets/images/icons/menu-icons/ic-members.svg",
        path: "/pages/members",
        name: "Members",
        iseditable: 1,
        isvisible: 1,
        order: 9,
      },
      {
        key: TabKeyEnum.ContactCard,
        iconPath: "/assets/images/icons/menu-icons/ic-contact-card.svg",
        path: "/pages/contact-card",
        name: "ContactCard",
        iseditable: 1,
        isvisible: 1,
        order: 10,
      },
      {
        key: TabKeyEnum.Boards,
        iconPath: "/assets/images/icons/menu-icons/ic-boards.svg",
        path: "/pages/boards",
        name: "Boards",
        iseditable: 1,
        isvisible: 1,
        order: 11,
      },
      {
        key: TabKeyEnum.EngagementPro,
        iconPath: "/assets/images/icons/menu-icons/icon-menu-engagement.svg",
        path: "/pages/engagement/project/list",
        name: "Projects",
        iseditable: 1,
        isvisible: 1,
        order: 12,
      },
      // {
      //   key: TabKeyEnum.PlannexeSpace,
      //   iconPath: "/assets/images/icons/menu-icons/icon-menu-engagement.svg",
      //   path: "/pages/engagement/space",
      //   name: "Deliverables",
      //   iseditable: 1,
      //   isvisible: 1,
      //   order: 12,
      // },
      {
        key: TabKeyEnum.Dashboards,
        iconPath: "/assets/images/custom-tab/icon-menu-dashboard.svg",
        path: "/pages/dashboards",
        name: "Dashboards",
        iseditable: 1,
        isvisible: 1,
        order: 13,
      },
      {
        key: TabKeyEnum.Vacancies,
        iconPath: "/assets/images/icons/menu-icons/ic-vacancies.svg",
        path: "/pages/vacancies",
        name: "Recruitment",
        iseditable: 1,
        isvisible: 1,
        order: 14,
      },
      {
        key: TabKeyEnum.Intajy,
        iconPath: "/assets/images/icons/menu-icons/ic-intajy.svg",
        path: "/pages/intajy",
        name: "Intajy",
        viewOn: "Web",
        iseditable: 1,
        isvisible: 1,
        order: 15,
      },
      // {
      //   key: TabKeyEnum.CustomersService,
      //   iconPath: "/assets/images/custom-tab/8.svg",
      //   name: "Reception",
      //   viewOn: "Web",
      //   iseditable: 1,
      //   isvisible: 1,
      //   isFixed: "1",
      //   order: 0,
      //   utilityType: 'floatingTab'
      // },
      {
        key: TabKeyEnum.Blog,
        iconPath: "/assets/images/icons/menu-icons/ic-blog.svg",
        path: "/pages/blog",
        name: "Blog",
        // viewOn: "Web",
        iseditable: 1,
        isvisible: 1,
        order: 16,
      },
      {
        key: TabKeyEnum.Sellexe,
        iconPath: "/assets/images/icons/menu-icons/ic-blog.svg",
        path: "/pages/sellexe",
        name: "Sellexe",
        iseditable: 1,
        isvisible: 1,
        order: 16,
      },
      {
        key: TabKeyEnum.ForYou,
        iconPath: "/assets/images/icons/menu-icons/ic-blog.svg",
        path: "/pages/for-you",
        name: "For You",
        iseditable: 1,
        isvisible: 0,
        order: 17,
      },
      {
        key: TabKeyEnum.SimpleStrataReview,
        iconPath: "/assets/images/icons/menu-icons/ic-activities.svg",
        path: "/pages/review/performance-period/listing",
        name: "Review",
        iseditable: 1,
        isvisible: 1,
        order: 18,
      },
      {
        key: TabKeyEnum.ConnecteXeDedicationReport,
        iconPath: "/assets/images/icons/menu-icons/ic-activities.svg",
        path: "/pages/dedication/mine",
        name: "ConnecteXe",
        iseditable: 1,
        isvisible: 1,
        order: 19,
      },
    ];
    if (filter?.key) {
      return defaultTabs.filter((t) => t.key === filter?.key);
    }
    if (filter?.excluded?.length) {
      return defaultTabs.filter(
        (t) => !filter?.excluded?.includes(t.key as TabKeyEnum)
      );
    }
    return defaultTabs;
  }

  getFooterTabs(filter?: {
    key?: string;
    excluded?: TabKeyEnum[];
  }): TabInterface[] {
    const defaultTabs: TabInterface[] = [
      {
        key: TabKeyEnum.Chat,
        iconPath: "/assets/images/icons/menu-icons/ic-messages.svg",
        path: "/pages/messages",
        name: "Chat",
        iseditable: 1,
        isvisible: 1,
        viewOn: "Web",
        order: 2,
        utilityType: "footerUtilities",
      },
      // {
      //   key: TabKeyEnum.Search,
      //   iconPath: "/assets/images/search-icon.svg",
      //   path: "/pages/search",
      //   name: "Search",
      //   iseditable: 1,
      //   isvisible: 1,
      //   viewOn: "Web",
      //   order: 2,
      //   utilityType: "footerUtilities",
      // },

      {
        key: TabKeyEnum.Language,
        iconPath: "/assets/images/icons/menu-icons/ic-messages.svg",
        path: "/pages/messages",
        name: "Language",
        iseditable: 0,
        isvisible: 1,
        viewOn: "Web",
        order: 1,
        utilityType: "footerUtilities",
      },

      {
        key: TabKeyEnum.Profile,
        iconPath: "/assets/images/icons/menu-icons/ic-profile.svg",
        path: "/pages/profile",
        name: "Profile",
        iseditable: 1,
        isvisible: 1,
        viewOn: "Web",
        order: 3,
        utilityType: "footerUtilities",
      },
    ];
    if (filter?.key) {
      return defaultTabs.filter((t) => t.key === filter?.key);
    }
    if (filter?.excluded?.length) {
      return defaultTabs.filter(
        (t) => !filter?.excluded?.includes(t.key as TabKeyEnum)
      );
    }
    return defaultTabs;
  }

  convertCustomsTabToTabs(
    customTabs: PublicProperties[],
    isFooterType: boolean = false,
    tabs?: TabInterface[],
    addonID?: string
  ): TabInterface[] {
    return customTabs.map((tab) => {
      const customTab: TabInterface = {
        data: {
          icon: tab.icon,
          name: tab.name,
          productId: tab.productId,
          productName: tab.productName,
          slug: tab.slug,
          subtype: tab.subtype,
          viewOn: tab.viewOn?.split(",") || [],
          visibleBy: tab.visibleBy,
          hiddenFrom: tab.hiddenFrom,
          url: tab.iFrameURL,
          subTabs: [],
          tabLayout: tab?.tabLayout,
          tabDescription: tab.tabDescription,
        },
        allowFeedOptions: tab.allowFeedOptions ?? { likes: true, comments: true, announcer: true },
        isDefaultTab: isFooterType ? 0 : tab.isDefaultTab,
        iconPath: this.getCustomTabIcons(tab.icon)[0]?.path,
        path: "",
        name: tab.name,
        refTab: tab.refTab,
        key: isFooterType
          ? `footer tab${tab?.customTabId}`
          : `custom tab${tab?.customTabId ?? "homePage"}`,
        iseditable: 1,
        isvisible: 1,
        viewOn: tab.viewOn,
        visibleBy: tab.visibleBy,
        hiddenFrom: tab.hiddenFrom,
        customKey: tab["customKey"],
        isLandingPage: isFooterType ? false : tab.isLandingPageTab,
        feedStream: tab?.feedStream ?? '',
        feedStreamDesign: tab?.feedStreamDesign,
        queryParams: {},
      };
      if (tab.subtype !== SubTypeEnum.NAVIGATION_TAB && tab.subtype !== SubTypeEnum.TOPIC && tab.subtype !== SubTypeEnum.LIBRARY && !tab?.urlName) {
        customTab.queryParams = {
          tabId: tab.customTabId,
        };
      } else if (tab.subtype == SubTypeEnum.REQUEST || tab.subtype == SubTypeEnum.RequestCatalog) {
        //temporarily add tabId until esp custom tabs are moved to new url strategy
        customTab.queryParams = {
          tabId: tab.customTabId,
        };
      } else if (tab.subtype == SubTypeEnum.REQUESTS || tab.subtype == SubTypeEnum.ACTIVITIES || tab.subtype == SubTypeEnum.SCORE_CARD) {
        customTab.dividers = tab.dividers;
        customTab.queryParams = {
          tabId: tab.customTabId,
        };
      }
      if (tab.utilityType) {
        customTab.utilityType = tab.utilityType;
      }

      switch (tab.subtype) {
        case SubTypeEnum.TOPIC:
          const libBasePath = this.getHeaderTabs({ key: TabKeyEnum.Library })[0]?.path;
          if (tab?.urlName) {
            customTab.path = `/pages/${tab?.urlName}-lb`;
          }
          else if (tab.slug && tab.slug !== '') {
            customTab.path = `/pages/${tab.slug}-lb`;
          } else {
            customTab.path = `${libBasePath}/details/${tab.productId}`;
          }
          if (customTab.isDefaultTab == 1) {
            customTab.queryParams.isdefault = true;
            const defaultTabSlug = this.handlingResourcesAndConnectexeSlug(tab.productId);
            if (!tab.slug) {
              customTab.path = `/pages/${defaultTabSlug}-lb`
            }
          }
          return customTab;
        case SubTypeEnum.LIBRARY:
          const libBasePathTt = this.getHeaderTabs({ key: TabKeyEnum.Library })[0]?.path;
          customTab.path = `${libBasePathTt}/topictype/${tab.productId}`;
          if (customTab.isDefaultTab == 1) {
            customTab.queryParams.isdefault = true;
            const defaultTabSlug = this.handlingResourcesAndConnectexeSlug(tab.productId);
            if (!tab.slug) {
              customTab.path = `/products/topictype/${defaultTabSlug}-lb`
            }
          }
          return customTab;
        case SubTypeEnum.REQUEST:
          // const param = tab?.urlName ?? tab.productId;
          // customTab.path = `/pages/defapplications/${param}-r`;
          // Normal request definition route – no special single-tab query params here
          customTab.path = `/pages/defapplications/${tab.productId}`;
          return customTab;
        case SubTypeEnum.REQUESTS:
          customTab.path = `/pages/applications`;
          return customTab;
        case SubTypeEnum.ACTIVITIES:
          customTab.path = `/pages/activities`;
          return customTab;
        case SubTypeEnum.SCORE_CARD:
          customTab.path = `/pages/scoreCard`;
          return customTab;
        case SubTypeEnum.FEEDS:
          if (tab?.feedStreamDesign == StreamDesignType.Blog) {
            customTab.path = `/pages/${tab?.urlName ?? tab.customTabId}-b`;
          } else {
            customTab.path = `/pages/${tab?.urlName ?? tab.customTabId}-f`;
          }
          return customTab;
        case SubTypeEnum.RequestCatalog:
          customTab.path = `/pages/espcatalog`;
          customTab.queryParams.definitionIds = tab.productId ?? "";
          return customTab;
        case SubTypeEnum.DASHBOARD:
          customTab.path = `/pages/${tab?.urlName ?? 'dashboards'}-db/details/${tab.productId}`;
          return customTab;
        case SubTypeEnum.IFRAME:
          const baseUrl = '/pages';
          customTab.path = tab?.urlName ? `${baseUrl}/${tab.urlName}-i` : `${baseUrl}/external/${tab.customTabId}`;
          return customTab;
        case SubTypeEnum.TILDA:
          const _baseUrl = '/pages';
          customTab.path = tab?.urlName ? `${_baseUrl}/${tab.urlName}-i` : `${_baseUrl}/external/${tab.customTabId}`;
          return customTab;
        case SubTypeEnum.WEB_LINK:
          customTab.path = tab.weblink;
          return customTab;
        case SubTypeEnum.NAV_GROUP:
          customTab.path = "";
          customTab.data.subTabs = [];
          return customTab;
        case SubTypeEnum.NAVIGATION_TAB:
          if (tabs?.length) {
            let referenceTab = tabs.find((t) => t?.key == tab?.refTab);
            if (referenceTab) {
              customTab.path = referenceTab?.path;

              if (
                referenceTab?.data?.subtype == SubTypeEnum.TOPIC ||
                referenceTab?.data?.subtype == SubTypeEnum.LIBRARY ||
                referenceTab?.data?.subtype == SubTypeEnum.IFRAME ||
                referenceTab?.data?.subtype == SubTypeEnum.FEEDS
              ) {
                customTab.queryParams = referenceTab?.queryParams;
              } else if (
                referenceTab?.data?.subtype == SubTypeEnum.RequestCatalog
              ) {
                customTab.queryParams.definitionIds = tab.productId ?? "";
              }
            }
          }
          return customTab;
        // Change 2
        default:
          customTab.path = `/pages/products/details/${tab?.productId}`;
          return customTab;
      }
    });
  }

  isIconVisible(viewType: string, tab: TabInterface): boolean {
    if (tab?.viewOn?.length > 0) {
      return tab?.viewOn?.includes(viewType)
    } else if (tab?.data?.viewOn?.length) {
      return tab?.data?.viewOn?.includes(viewType)
    } else if (tab?.data?.viewOn?.length === 0) {
      return false
    } else return tab?.viewOn?.length !== 0;
  }

  handlingResourcesAndConnectexeSlug(prodId: string) {
    const resourcesProductId: string = environment.isProduction ?
      'e0fb6698-0eeb-43ed-a5e7-13ddae3411f6' :
      'a724b30f-29e7-44e0-98ea-1d1ce730e146';
    const connectexeProductSlug = environment.isProduction ? 'Resource-Hub' : 'Ramadan';
    const connectexeProductId = environment.isProduction ?
      '76930cea-7781-486f-9c19-fb13c5e0e08b' :
      '5f740294-9afb-43de-bfac-939590d57380';

    if (prodId == resourcesProductId) {
      return 'Ramadan';
    }
    else if (prodId == connectexeProductId) {
      return connectexeProductSlug;
    }
  }

  isFileImage(file: File): boolean {
    return file && file["type"].split("/")[0] === "image";
  }

  getSocialIconsMap(): Map<SocialNetworkEnum, SocialIconPathEnum> {
    const socialIconsArr = [
      { name: SocialNetworkEnum.TWITTER, path: SocialIconPathEnum.TWITTER },
      { name: SocialNetworkEnum.FACEBOOK, path: SocialIconPathEnum.FACEBOOK },
      { name: SocialNetworkEnum.LINKEDIN, path: SocialIconPathEnum.LINKEDIN },
      { name: SocialNetworkEnum.INSTAGRAM, path: SocialIconPathEnum.INSTAGRAM },
    ];
    const socialIconsMap: Map<SocialNetworkEnum, SocialIconPathEnum> = new Map<
      SocialNetworkEnum,
      SocialIconPathEnum
    >();
    socialIconsArr.forEach((icon) => {
      socialIconsMap.set(icon.name, icon.path);
    });
    return socialIconsMap;
  }

  setActiveEnumData(message: string, id?: number) {
    this.routerData.next(message);
  }

  getActiveEnumData(): Observable<any> {
    return this.routerData.asObservable();
  }

  setActiveTeamCardEnum(message: string, id?: number) {
    this.routerTeamData.next(message);
  }

  getActiveTeamCardEnum(): Observable<any> {
    return this.routerTeamData.asObservable();
  }

  setDefaultTitleAndFavicon(title?: string) {
    if (title && title.trim() != "") {
      //if title is set explicitly than use it
      this.titleService.setTitle(title);
    } else if (environment.build == ENV_BUILD.STEMEXE) {
      //if it's stemexe build

      if (environment.enableGroupsSelection) {
        //set default stemxe title if group selection is enabled
        this.titleService.setTitle(PageTitleEnum.StemexeTitle);
        this.document
          .getElementById("appFavicon")
          .setAttribute("href", "assets/stemexe-fav.png");
        //
      } else if (this.authService.idenediGroupId) {
        let request$ = this.groupsService
          .getUserGroupsProfiles([this.authService.idenediGroupId])

        request$
          .subscribe((groupInfo) => {
            if (groupInfo.FirstName && groupInfo.FirstName != "") {
              this.titleService.setTitle(`${groupInfo.FirstName} | StemeXe`);
            } else {
              this.titleService.setTitle(PageTitleEnum.StemexeTitle);
            }

            const smallProfileImageUrl = groupInfo.ProfileImageUrl?.replace('/profileimages/', '/profileimages-small/');

            const favicon =
              smallProfileImageUrl ?? "assets/stemexe-fav.png";

            this.document
              .getElementById("appFavicon")
              .setAttribute("href", favicon);
          });
      } else {
        this.titleService.setTitle(PageTitleEnum.StemexeTitle);
      }
    } else if (environment.build == ENV_BUILD.EXCEEDERS) {
      this.titleService.setTitle(PageTitleEnum.ExceedersTitle);
      this.document
        .getElementById("appFavicon")
        .setAttribute("href", "assets/exceeders-fav.jpg");
    } else {
      this.titleService.setTitle(PageTitleEnum.StemexeTitle);
      this.document
        .getElementById("appFavicon")
        .setAttribute("href", "assets/stemexe-fav.png");
    }
  }
  isLinkExternal(url: string) {
    try {
      if (isPlatformBrowser(this.platformId)) {
        url = url.trim();
        if (url.startsWith('www.')) {
          url = url.replace('www.', 'http://');
        } else if (!url.startsWith('http') &&
          !url.startsWith("tel:") &&
          !url.startsWith("sms:") &&
          !url.startsWith("mailto:")) {
          let a = url.indexOf('.');
          let b = url.indexOf('/');
          if (a == -1) { a = 9999 };
          if (b == -1) { b = 9999 };
          const isUrl = a < b;
          if (isUrl) {
            url = 'http://' + url
          }
        }

        const urlDomain = new URL(url).host.replace("www.", "");
        const currentDomain = window.location.host.replace("www.", "");
        return urlDomain != currentDomain;
      }
      return false;
    } catch (err) {
      return false;
    }
  }

  initializeVirtualRoom(virtualRoomAddon: Addon) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (virtualRoomAddon.PublicProperties.RoomId) {
      window["vr_roomId"] = virtualRoomAddon?.PublicProperties?.RoomId ?? '';
      this.appendScript("assets/js/intajy-virtual-room.js");
    }
  }

  removeVirtualRoom() {
    document
      .querySelector('head script[src="assets/js/intajy-virtual-room.js"]')
      ?.remove();

    let roomDiv = this.document?.getElementById("guest-room-iframe");
    if (roomDiv) {
      roomDiv.remove();
    }
    delete window["vr_roomId"];
  }

  private appendScript(filepath) {
    if (document.querySelector('head script[src="' + filepath + '"]')) {
      return;
    }

    var ele = document.createElement("script");
    ele.setAttribute("type", "text/javascript");
    ele.setAttribute("src", filepath);
    ele.setAttribute("async", 'true');
    this.document.head.appendChild(ele);
  }

  getTodaysAttendance(): Observable<any> {
    const url = `/attendance/today`;
    const headerOptions = {
      headers: this.authService.buildAuthHeader(AuthType.ESP),
    };
    return this.espTokenResolver.resolve().pipe(
      switchMap((addon) => {
        return this.http.get(url, headerOptions);
      })
    );
  }
  getHeadersWithoutoken() {
    let headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json');
    headers.append(AUTH_TYPE_HEADER, AuthType.ESP);
    return headers;
  }
  
  getTodaysAttendanceV2(): Observable<any> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const url = `/attendance/todayV2`;
    const body: any = {
      timeZone,
      lateArrivalMessage: ''
    };
    const headerOptions = {
      headers: this.authService.buildAuthHeader(AuthType.ESP),
    };
    return this.espTokenResolver.resolve().pipe(
      switchMap((addon) => {
        return this.http.post(url, body, headerOptions);
      })
    );
  }

  clockAttendance(clockType, lateArrivalMessage: string = '', sectionValues?: any[]) {

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const url = `/attendance/${clockType}`;
    const body :any = {
      timeZone,
      lateArrivalMessage
    };
    if (sectionValues && sectionValues.length > 0) {
      body.sectionValues = sectionValues;
    }
    const headerOptions = {
      headers: this.authService.buildAuthHeader(AuthType.ESP),
    };
    return this.authService.ensureEspToken().pipe(
      switchMap((addon) => {
        if (!addon) {
          return of(null);
        }
        return this.http.put(url, body, headerOptions);
      })
    );
  }

  initTabCustomizationList(fromCreateGroup: boolean): Observable<any> {
    let allTabs = [];
    return this.authService.getAddonsAuth(this.authService.idenediGroupId).pipe(
      map((addons) => {
        console.log('initTabCustomizationListaddons', addons)
        const customTabAddons = addons.filter(
          (addon) => addon.Type == AuthType.CustomTab
        );
        const footerTabAddons = addons.filter(
          (addon) => addon.Type == AuthType.Footer
        );
        const tabOrderAddon = addons.find(
          (addon) => addon.Type === AuthType.TabOrder
        );
        const floatingTabsAddon = addons.find(
          (addon) => addon.Type === AuthType.VirtualRoom
        );
        let customTabs = this.convertCustomsTabToTabs(
          customTabAddons.map((c) => c.PublicProperties)
        ).filter((t) => !t.isLandingPage);

        let footerTabs = this.convertCustomsTabToTabs(
          footerTabAddons.map((c) => c.PublicProperties),
          true
        );
        let tabOrder =
          (tabOrderAddon?.PublicProperties?.jsonData?.data as TabInterface[]) ||
          [];
        let utilityIconTabOrder =
          (tabOrderAddon?.PublicProperties?.jsonData
            ?.utilities as TabInterface[]) || [];
        let utilityTextTabOrder =
          (tabOrderAddon?.PublicProperties?.jsonData
            ?.utilitiesText as TabInterface[]) || [];
        let footerTabOrder =
          (tabOrderAddon?.PublicProperties?.jsonData
            ?.footer as TabInterface[]) || [];
        let utilityFooterTabOrder =
          (tabOrderAddon?.PublicProperties?.jsonData
            ?.footerUtilities as TabInterface[]) || [];
        let socialFooterUtilityTabOrder =
          (tabOrderAddon?.PublicProperties?.jsonData
            ?.socialFooterUtilities as TabInterface[]) || [];
        let utilityfloatingTabOrder =
          (tabOrderAddon?.PublicProperties?.jsonData
            ?.floatingTab as TabInterface[]) || [];
        // Add home tab order list
        let homeTabOrder =
          (tabOrderAddon?.PublicProperties?.jsonData
            ?.home as TabInterface[]) || [];

        if (!fromCreateGroup) {
          allTabs = [];
        }

        utilityIconTabOrder = utilityIconTabOrder.map((t) => {
          t.utilityType = "icon";
          return t;
        });
        utilityTextTabOrder = utilityTextTabOrder.map((t) => {
          t.utilityType = "text";
          return t;
        });
        utilityFooterTabOrder = utilityFooterTabOrder.map((t) => {
          t.utilityType = "footerUtilities";
          return t;
        });
        socialFooterUtilityTabOrder = socialFooterUtilityTabOrder.map((t) => {
          t.utilityType = "socialFooterUtility";
          return t;
        });
        footerTabOrder = footerTabOrder.map((t) => {
          t.utilityType = "footer";
          return t;
        });
        utilityfloatingTabOrder = utilityfloatingTabOrder.map((t) => {
          t.utilityType = "floatingTab";
          return t;
        });

        tabOrder = tabOrder
          .concat(utilityIconTabOrder)
          .concat(utilityfloatingTabOrder)
          .concat(utilityTextTabOrder);
        // Keep social footer utilities in the footer ordering stream so moved footer custom tabs
        // are still represented in allTabs, then split by utilityType in tab-customization.
        footerTabOrder = footerTabOrder.concat(utilityFooterTabOrder).concat(socialFooterUtilityTabOrder);

        if (tabOrder.length) {
          const customTabInTabOrder = tabOrder.filter((t) =>
            t.key?.startsWith("custom")
          );

          const FooterTabInTabOrder = footerTabOrder?.filter((t) =>
            t?.key?.startsWith("footer")
          );

          if (customTabInTabOrder.length) {
            customTabs = this.mappingTabWithTabOrder(
              customTabs,
              customTabInTabOrder
            );
          }

          if (FooterTabInTabOrder?.length) {
            footerTabs = this.mappingTabWithTabOrder(
              footerTabs,
              FooterTabInTabOrder
            );
          }

          // Do not add those which are in the home array of tabOrderAddon
          // const customTabWithoutOrder = customTabs.filter(
          //   (c) => !c.hasOwnProperty("order") && !c.isLandingPage
          // );
          // Do not add those which are in the home array of tabOrderAddon
          const customTabWithoutOrder = customTabs.filter(
            (c) =>
              !c.hasOwnProperty("order") &&
              !c.isLandingPage &&
              !(tabOrderAddon.PublicProperties.jsonData.home?.some(homeTab => homeTab.key === c.key) || false)
          );

          const footerTabWithoutOrder = footerTabs.filter(
            (c) => !c.hasOwnProperty("order")
          );

          const sortedInitialTab = tabOrder.filter(
            (c) => !c.key?.startsWith("custom")
          );

          const sortedInitialFooterTab = footerTabOrder.filter(
            (ft) => !ft.key?.startsWith("footer")
          );

          const excludedTabs = [TabKeyEnum.Messages, TabKeyEnum.Boards];
          const allowedKeys = ['chat', 'language', 'socialmedia', 'espAttendance'];

          const allExcludedKeys = [
            ...utilityIconTabOrder.map(t => t.key),
            ...utilityTextTabOrder.map(t => t.key),
            ...utilityfloatingTabOrder.map(t => t.key),
            ...homeTabOrder.map(t => t.key),
            ...socialFooterUtilityTabOrder.map(t => t.key),
          ].filter(key => !allowedKeys.includes(key)); // Exclude allowed keys

          const unsortedInitialTabs = this.getHeaderTabs({ excluded: excludedTabs })
            .filter(t =>
              !sortedInitialTab.map(tab => tab.key).includes(t.key) &&
              !allExcludedKeys.includes(t.key) // Additional filtering condition
            );

          const unsortedInitialFooterTab = this.getFooterTabs().filter(
            (t) => !sortedInitialFooterTab.map((t) => t.key).includes(t.key)
          );

          allTabs = [
            ...sortedInitialTab,
            ...customTabs,
            ...unsortedInitialTabs,
            ...footerTabs,
            ...sortedInitialFooterTab,
            ...unsortedInitialFooterTab,
          ]
            .filter((t) => t.hasOwnProperty("order"))
            .sort((a, b) => a.order - b.order);
          allTabs = [
            ...allTabs,
            ...customTabWithoutOrder,
            ...footerTabWithoutOrder,
          ];
        }
        if (!tabOrder.length) {
          allTabs = [
            ...this.getHeaderTabs({
              excluded: [TabKeyEnum.Messages, TabKeyEnum.Boards],
            }),
            ...customTabs,
            ...footerTabs,
            ...this.getFooterTabs(),
          ];
        }
        console.log('initTabCustomizationListallTabs', allTabs)
        return {
          tabs: allTabs,
          tabOrderAddon: tabOrderAddon,
          homePageAddon: addons.find(
            (addon) =>
              addon.Type == AuthType.CustomTab &&
              addon?.PublicProperties.isLandingPageTab
          ),
        };
      })
    );
  }

  mappingTabWithTabOrder(tabs: TabInterface[], tabsInTabOrder) {
    return tabs
      .filter((t) => !t.isLandingPage)
      .map((tab) => {
        const foundTab = tabsInTabOrder.find((tto) => tto.key === tab.key);
        if (foundTab) {
          // customTab.name = foundTab.name;
          tab.data = foundTab.data;
          tab.order = foundTab.order;
          foundTab.viewOn = tab.viewOn;
          foundTab.name = tab.name;
          foundTab.visibleBy = tab.visibleBy;
          foundTab.allowFeedOptions = tab?.allowFeedOptions || { likes: true, comments: true, announcer: true };
          foundTab.refTab = tab.refTab;
          return foundTab;
        }
        tab.isvisible = 1;
        tab.iseditable = tab?.isLandingPage ? 0 : 1;
        return tab;
      });
  }

  filterTabsByConnectedApps(tabs: TabInterface[]) {
    return this.authService.getAddonsAuth(this.authService.idenediGroupId).pipe(
      map((addons) => {
        let isEspEnabled = addons.some(
          (a) => a.Type.toLowerCase() === AuthType.ESP.toLowerCase()
        );
        let isTechnadoptEnabled = addons.some(
          (a) => a.Type.toLowerCase() === AuthType.Technadopt.toLowerCase()
        );
        let isHigherEnabled = addons.some(
          (a) => a.Type.toLowerCase() === AuthType.Higher.toLowerCase()
        );
        let isPowerbiEnabled = addons.some(
          (a) => a.Type.toLowerCase() === AuthType.PowerBi.toLowerCase()
        );
        let isSimpleStrataEnabled = addons.some(
          (a) => a.Type.toLowerCase() === AuthType.SimpleStrata.toLowerCase()
        );
        let isIntajyAddonEnabled = addons.some(
          (a) => a.Type.toLowerCase() === AuthType.Intajy.toLowerCase()
        );
        let isConnectEXEEnabled = addons.some(
          (a) => a.Type.toLocaleLowerCase() === AuthType.VirtualRoom.toLocaleLowerCase()
        )
        let isEngagementProEnabled = addons.some(
          (a) => a.Type.toLowerCase() === AuthType.EngagementPro.toLowerCase()
        );
        let isSellexeEnabled = addons.some(
          (a) => a.Type.toLowerCase() === AuthType.OppPro.toLowerCase()
        );
        tabs = tabs.filter(
          (tab) =>
            tab.key !== TabKeyEnum.Boards && tab?.refTab !== TabKeyEnum.Boards
        );
        if (!isEspEnabled) {
          tabs = tabs.filter(
            (tab) =>
              tab.key !== TabKeyEnum.Requests &&
              tab.key !== TabKeyEnum.EspRecords &&
              tab.key !== TabKeyEnum.EspSurveys &&
              tab.key !== TabKeyEnum.EspAttendance &&
              tab?.refTab !== TabKeyEnum.Requests &&
              tab?.refTab !== TabKeyEnum.EspRecords &&
              tab?.refTab !== TabKeyEnum.EspSurveys
          );
        }
        if (!isTechnadoptEnabled) {
          tabs = tabs.filter(
            (tab) =>
              tab.key !== TabKeyEnum.Library &&
              tab?.refTab !== TabKeyEnum.Library
          );
        }
        if (!isEngagementProEnabled) {
          tabs = tabs.filter(
            (tab) =>
              tab.key !== TabKeyEnum.EngagementPro &&
              tab?.refTab !== TabKeyEnum.EngagementPro
          );
        }
        if (!isSellexeEnabled) {
          tabs = tabs.filter(
            (tab) =>
              tab.key !== TabKeyEnum.Sellexe &&
              tab?.refTab !== TabKeyEnum.Sellexe
          );
        }
        if (!isHigherEnabled) {
          tabs = tabs.filter(
            (tab) =>
              tab.key !== TabKeyEnum.Vacancies &&
              tab?.refTab !== TabKeyEnum.Vacancies
          );
        }
        if (!isPowerbiEnabled) {
          tabs = tabs.filter(
            (tab) =>
              tab.key !== TabKeyEnum.Dashboards &&
              tab?.refTab !== TabKeyEnum.Dashboards
          );
        }
        if (!isSimpleStrataEnabled) {
          tabs = tabs.filter(
            (tab) =>
              tab.key !== TabKeyEnum.Activities &&
              tab.key !== TabKeyEnum.ScoreCard &&
              tab.key !== TabKeyEnum.ssnotes &&
              tab.key !== TabKeyEnum.SimpleStrataReview &&
              tab?.refTab !== TabKeyEnum.Activities &&
              tab?.refTab !== TabKeyEnum.ScoreCard &&
              tab?.refTab !== TabKeyEnum.ssnotes &&
              tab?.refTab !== TabKeyEnum.SimpleStrataReview
          );
        }
        if (!isIntajyAddonEnabled) {
          tabs = tabs.filter(
            (tab) =>
              tab.key !== TabKeyEnum.Intajy && tab?.refTab !== TabKeyEnum.Intajy
          );
        }
        if (!isConnectEXEEnabled) {
          tabs = tabs.filter(
            (tab) =>
              tab.key !== TabKeyEnum.CustomersService &&
              tab.key !== TabKeyEnum.ConnecteXeDedicationReport &&
              tab?.refTab !== TabKeyEnum.CustomersService &&
              tab?.refTab !== TabKeyEnum.ConnecteXeDedicationReport
          );
        }
        tabs = this.excludeCustomTabsForDisconnectedSources(tabs, {
          isEspEnabled,
          isSimpleStrataEnabled,
        });
        return tabs;
      })
    );
  }

  private excludeCustomTabsForDisconnectedSources(
    tabs: TabInterface[],
    sources: { isEspEnabled: boolean; isSimpleStrataEnabled: boolean }
  ): TabInterface[] {
    const espSubtypes = [
      SubTypeEnum.REQUEST,
      SubTypeEnum.REQUESTS,
      SubTypeEnum.RequestCatalog,
    ];
    const simpleStrataSubtypes = [
      SubTypeEnum.ACTIVITIES,
      SubTypeEnum.SCORE_CARD,
    ];
    const removedKeys = new Set<string>();

    tabs = tabs.filter((tab) => {
      const rawSubtype = tab?.data?.subtype ?? tab?.subtype;
      const subtype = rawSubtype == null ? NaN : Number(rawSubtype);
      const hidden =
        (!sources.isEspEnabled && espSubtypes.includes(subtype)) ||
        (!sources.isSimpleStrataEnabled && simpleStrataSubtypes.includes(subtype));
      if (hidden && tab?.key) {
        removedKeys.add(tab.key);
      }
      return !hidden;
    });

    if (removedKeys.size) {
      tabs = tabs.filter((tab) => !tab?.refTab || !removedKeys.has(tab.refTab));
    }
    return tabs;
  }

  createConnectexeTab() {
    let dummyCustomAddon: Addon = new Addon();
    const productName = environment.isProduction ? 'Resource Hub' : 'ramadan';
    const productSlug = environment.isProduction ? 'resource-hub' : 'ramadan';
    const productId = environment.isProduction ? '76930cea-7781-486f-9c19-fb13c5e0e08b' : '5f740294-9afb-43de-bfac-939590d57380';
    dummyCustomAddon.PublicProperties = {
      groupIdenedi: this.authService.idenediGroupId,
      icon: 4,
      title: 'ConnecteXe',
      name: 'ConnecteXe',
      jsonData: "{\n\n}",
      viewOn: 'Mobile, Web',
      productId: productId,
      productName: productName,
      slug: productSlug,
      isDefaultTab: 1,
      subtype: SubTypeEnum.TOPIC
    };
    return this.authService.createNewAddon(dummyCustomAddon, this.authService.idenediGroupId, AuthType.CustomTab).pipe(map(instanceId => {
      dummyCustomAddon.InstanceId = instanceId
      return dummyCustomAddon;
    }))
  }

  stringifyArEn(arabic: string, english: string, isArabic): string {
    let data;

    if (isArabic) {
      data = {
        ar: english,
        en: arabic
      };
    }
    else {
      data = {
        ar: arabic,
        en: english
      };
    }

    return JSON.stringify(data);
  }



  getDataForLanguage(jsonData: any, isArabic: boolean): string {
    let parsedData;
    try {
      parsedData = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;

    } catch (error) {

      return jsonData;
    }

    if (isArabic) {

      if ((parsedData?.en == undefined || parsedData?.en == null || parsedData?.en.length == 0) && (parsedData?.ar == undefined || parsedData?.ar == null || parsedData?.ar.length == 0)) {
        return parsedData
      }

      if (!parsedData?.ar || !parsedData?.ar.trim()) {
        if (parsedData?.en == undefined || parsedData?.en == null || parsedData?.en.length == 0) {
          return parsedData?.ar;
        }
        else {
          return parsedData?.en;
        }
      }
      else {
        if (parsedData?.ar == undefined || parsedData?.ar == null || parsedData?.ar.length == 0) {
          return parsedData?.en;
        }
        else {
          return parsedData?.ar;
        }

      }
    } else {
      if ((parsedData?.en == undefined || parsedData?.en == null || parsedData?.en.length == 0) && (parsedData?.ar == undefined || parsedData?.ar == null || parsedData?.ar.length == 0)) {
        return parsedData
      }
      if (parsedData?.en == undefined || parsedData?.en == null || parsedData?.en.length == 0) {
        return parsedData?.ar;
      }
      else {
        return parsedData?.en;
      }

    }
  }

  getDataForInput(jsonData: any, langPripority): string {
    let parsedData;
    try {
      parsedData = typeof jsonData == 'string' ? JSON.parse(jsonData) : jsonData;
    } catch (error) {
      // If parsing fails, jsonData might not be valid JSON
      // Return the original jsonData
      return jsonData;
    }

    if (langPripority === 'ar') {
      return parsedData?.ar || parsedData?.en;
    } else {
      return parsedData?.en || parsedData?.ar;
    }
  }


  isJSON(str) {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }


  conevrtToArabicMonthText(input): string {
    const keywords = {
      'January': 'يناير',
      'February': 'فبراير',
      'March': 'مارس',
      'April': 'أبريل',
      'May': 'مايو',
      'June': 'يونيو',
      'July': 'يوليو',
      'August': 'أغسطس',
      'September': 'سبتمبر',
      'October': 'أكتوبر',
      'November': 'نوفمبر',
      'December': 'ديسمبر',
      'Week': 'أسبوع',
      'Quarter': 'ربع'
    };

    if (!!input) {
      for (const key in keywords) {
        if (input.includes(key)) {
          input = input.replace(new RegExp(key, 'g'), keywords[key]);
        }
      }
    }

    return input;

  }

  transformDateToArabic(date: any): string {
    if (date) {
      const monthsMap = {
        'January': 'يناير',
        'February': 'فبراير',
        'March': 'مارس',
        'April': 'أبريل',
        'May': 'مايو',
        'June': 'يونيو',
        'July': 'يوليو',
        'August': 'أغسطس',
        'September': 'سبتمبر',
        'October': 'أكتوبر',
        'November': 'نوفمبر',
        'December': 'ديسمبر'
      };

      const start = new Date(date);

      const formattedStart = `${start.getDate()} ${monthsMap[start.toLocaleString('default', { month: 'long' })]} ${start.getFullYear()}`;
      return formattedStart;
    } else {
      return ''; // Return an empty string or any default value when the date is empty
    }
  }

  // [ { "Id": 0, "Name": "Read" }, { "Id": 1, "Name": "Create" }, { "Id": 2, "Name": "Update" }, { "Id": 3, "Name": "Delete" }, { "Id": 4, "Name": "Append" }, { "Id": 5, "Name": "SpecialRights" } ]
  isAllowedAction(allowedActions: any[], actionId: number): boolean {
    for (const action of allowedActions) {
      // Check if the current action's Id matches the provided actionId
      if (action.Id === actionId) {
        return true; // Return true if matched
      }
    }
    return false; // Return false if not matched

  }


  transform(value: number): string {
    if (value == null || isNaN(Number(value))) {
      return '';
    }
    const num = Number(value);
    if (num >= 1000000000) {
      let valb = (num / 1000000000).toFixed(2).replace(/\.00+$/, '') + 'B';
      if (valb.includes('.00')) {
        valb = valb.split('.00')[0] + 'B'
      }
      return valb;
    }
    else if (num >= 1000000) {
      let valm = (num / 1000000).toFixed(2).replace(/\.00+$/, '') + 'M';
      if (valm.includes('.00')) {
        valm = valm.split('.00')[0] + 'M'
      }
      return valm;
    }
    else if (num >= 1000) {
      let valk = (num / 1000).toFixed(2).replace(/\.00+$/, '') + 'K';
      if (valk.includes('.00')) {
        valk = valk.split('.00')[0] + 'K'
      }
      return valk;
    }
    else {
      return String(Number(num.toFixed(2)));
    }
  }

  getCurrentUrlForModule(module: string): string {
    const segments = this.router.url.split('?')[0].split('/').filter(Boolean);
    const idx = segments.lastIndexOf(module);
    if (idx !== -1) {
      const endIdx = Math.min(idx + 2, segments.length);
      return segments.slice(0, endIdx).join('/');
    }
    return segments.join('/');
  }

  setupFeedOptions(tabKey: string | undefined): Observable<FeedOptions> {
    const defaultOptions: FeedOptions = { likes: true, comments: true, announcer: true };

    if (!tabKey) {
      return of(defaultOptions);
    }

    return forkJoin({
      addons: this.initTabCustomizationList(false),
      authAddons: this.authService.getAddonsAuth(this.authService.idenediGroupId)
    }).pipe(
      map(({ addons, authAddons }) => {
        const authTab = authAddons?.find(t =>
          t?.PublicProperties?.customTabId === tabKey?.replace('custom tab', '')
        );

        if (authTab) {
          return authTab?.PublicProperties?.allowFeedOptions ?? defaultOptions;
        }

        const defaultTab = addons?.tabs?.find(t => t?.key === tabKey);
        return defaultTab?.allowFeedOptions ?? defaultOptions;
      })
    );
  }

}
